import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, collectionGroup, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "./firebaseConfig";

export type CampaignMediaType = "IMAGE" | "VIDEO";
export type CampaignPublishState = "DRAFT" | "PUBLISHED" | "PAUSED";
export type CampaignDisplayStatus = "DRAFT" | "SCHEDULED" | "ACTIVE" | "PAUSED" | "ENDED";
export type CampaignDeliveryStatus = "QUEUED" | "DOWNLOADING" | "APPLIED" | "FAILED";

export interface CampaignType {
    id: string;
    title: string;
    mediaType: CampaignMediaType;
    storagePath: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    assetVersion: number;
    imageDurationSec: number | null;
    targetKioskDocIds: string[];
    startAtMs: number | null;
    endAtMs: number | null;
    publishState: CampaignPublishState;
    revision: number;
    createdAtMs: number;
    updatedAtMs: number;
    publishedAtMs: number | null;
    archivedAtMs?: number | null;
    archivedBy?: string | null;
    createdBy: string;
    updatedBy: string;
}

export interface CampaignDelivery {
    campaignId: string;
    kioskDocId: string;
    campaignRevision: number;
    status: CampaignDeliveryStatus;
    updatedAtMs: number;
    errorCode?: string | null;
}

export interface CampaignSaveInput {
    campaignId: string;
    title: string;
    mediaType: CampaignMediaType;
    storagePath: string;
    fileName: string;
    assetVersion: number;
    imageDurationSec: number | null;
    targetKioskDocIds: string[];
    startAtMs: number | null;
    endAtMs: number | null;
}

interface CampaignContextType {
    campaigns: CampaignType[];
    deliveries: CampaignDelivery[];
    loading: boolean;
    saveCampaign: (input: CampaignSaveInput) => Promise<void>;
    publishCampaign: (campaignId: string) => Promise<void>;
    pauseCampaign: (campaignId: string) => Promise<void>;
    resumeCampaign: (campaignId: string) => Promise<void>;
    hideCampaign: (campaignId: string) => Promise<void>;
    unhideCampaign: (campaignId: string) => Promise<void>;
    discardCampaignDraft: (campaignId: string) => Promise<void>;
}

const CampaignContext = createContext<CampaignContextType | null>(null);

export const getCampaignDisplayStatus = (
    campaign: Pick<CampaignType, "publishState" | "startAtMs" | "endAtMs">,
    now = Date.now(),
): CampaignDisplayStatus => {
    if (campaign.endAtMs !== null && campaign.endAtMs <= now) return "ENDED";
    if (campaign.publishState === "DRAFT") return "DRAFT";
    if (campaign.publishState === "PAUSED") return "PAUSED";
    if (campaign.startAtMs !== null && campaign.startAtMs > now) return "SCHEDULED";
    return "ACTIVE";
};

const callCampaignFunction = async (name: string, data: Record<string, unknown>) => {
    await httpsCallable(functions, name)(data);
};

export const CampaignProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [campaigns, setCampaigns] = useState<CampaignType[]>([]);
    const [deliveries, setDeliveries] = useState<CampaignDelivery[]>([]);
    const [campaignsReady, setCampaignsReady] = useState(false);
    const [deliveriesReady, setDeliveriesReady] = useState(false);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "campaigns"),
            (snapshot) => {
                setCampaigns(
                    snapshot.docs
                        .map((item) => ({ ...item.data(), id: item.id } as CampaignType))
                        .sort((a, b) => b.createdAtMs - a.createdAtMs),
                );
                setCampaignsReady(true);
            },
            (error) => {
                console.error("캠페인 목록 구독 오류", error);
                setCampaignsReady(true);
            },
        );
        return unsubscribe;
    }, []);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            collectionGroup(db, "deliveries"),
            (snapshot) => {
                setDeliveries(snapshot.docs.map((item) => item.data() as CampaignDelivery));
                setDeliveriesReady(true);
            },
            (error) => {
                console.error("캠페인 배포 상태 구독 오류", error);
                setDeliveriesReady(true);
            },
        );
        return unsubscribe;
    }, []);

    const value = useMemo<CampaignContextType>(() => ({
        campaigns,
        deliveries,
        loading: !campaignsReady || !deliveriesReady,
        saveCampaign: (input) => callCampaignFunction("saveCampaign", input as unknown as Record<string, unknown>),
        publishCampaign: (campaignId) => callCampaignFunction("publishCampaign", { campaignId }),
        pauseCampaign: (campaignId) => callCampaignFunction("pauseCampaign", { campaignId }),
        resumeCampaign: (campaignId) => callCampaignFunction("resumeCampaign", { campaignId }),
        hideCampaign: (campaignId) => callCampaignFunction("hideCampaign", { campaignId }),
        unhideCampaign: (campaignId) => callCampaignFunction("unhideCampaign", { campaignId }),
        discardCampaignDraft: (campaignId) => callCampaignFunction("discardCampaignDraft", { campaignId }),
    }), [campaigns, deliveries, campaignsReady, deliveriesReady]);

    return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
};

export const useCampaign = () => {
    const context = useContext(CampaignContext);
    if (!context) throw new Error("useCampaign must be used within CampaignProvider");
    return context;
};
